import { useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { parseAmountInput } from '@/lib/shoppingItems';

interface AddOwnItemFormProps {
    pending: boolean;
    /** Resolves once the item is saved; the form clears only then. */
    onAdd: (item: { name: string; amount: number | null; unit: string }) => Promise<unknown>;
}

/**
 * "Add your own item": free text plus an optional amount. Saved as a free-text
 * line under "Added by you"; it never creates a catalogue ingredient.
 */
export function AddOwnItemForm({ pending, onAdd }: AddOwnItemFormProps) {
    const [name, setName] = useState('');
    const [amount, setAmount] = useState('');

    const submit = async (event: FormEvent) => {
        event.preventDefault();
        const trimmed = name.trim();
        if (!trimmed || pending) return;
        const parsed = parseAmountInput(amount);
        try {
            await onAdd({ name: trimmed, amount: parsed.amount, unit: parsed.unit });
            setName('');
            setAmount('');
        } catch {
            // The page shows the error; keep what was typed so it can be retried.
        }
    };

    return (
        <form
            onSubmit={submit}
            aria-labelledby="add-own-item-heading"
            className="rounded-xl border border-brand-surface-edge bg-brand-surface p-4 shadow-sm"
        >
            <h2 id="add-own-item-heading" className="mb-3 text-base font-bold text-foreground">
                Add your own item
            </h2>
            <label htmlFor="own-item-name" className="sr-only">
                What do you need?
            </label>
            <Input
                id="own-item-name"
                value={name}
                maxLength={200}
                placeholder="What do you need?"
                onChange={(event) => setName(event.target.value)}
                className="h-12 bg-white text-base"
            />
            <div className="mt-2 flex gap-2">
                <label htmlFor="own-item-amount" className="sr-only">
                    Amount (optional)
                </label>
                <Input
                    id="own-item-amount"
                    value={amount}
                    maxLength={60}
                    placeholder="Amount"
                    onChange={(event) => setAmount(event.target.value)}
                    className="h-12 min-w-0 flex-1 bg-white text-base"
                />
                <Button
                    type="submit"
                    disabled={pending || !name.trim()}
                    className="h-12 px-5 text-base font-bold"
                >
                    <Plus className="size-5" />
                    {pending ? 'Adding' : 'Add'}
                </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">Amount is optional.</p>
        </form>
    );
}
